import React, { useState, useEffect, useRef } from 'react';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
  Button,
  Spinner
} from 'reactstrap';
import { FaBell, FaCheck, FaCheckDouble, FaNewspaper, FaShoppingCart, FaExclamationTriangle } from 'react-icons/fa';
import API from '../utils/api';

const NotificationsDropdown = ({ notificationCount, onNotificationCountChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Load notifications when opening dropdown
      loadNotifications(true);
    }
  };

  const loadNotifications = async (reset = false) => {
    try {
      setLoading(true);
      const currentPage = reset ? 1 : page;

      const params = new URLSearchParams({
        page: currentPage,
        per_page: 10
      });

      const response = await API.get(`/notifications?${params.toString()}`);
      const newNotifications = response.data.notifications;

      if (reset) {
        setNotifications(newNotifications);
        setPage(1);
      } else {
        setNotifications(prev => [...prev, ...newNotifications]);
      }

      setHasMore(response.data.pagination.has_next);
      setPage(currentPage + 1);

    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await API.put(`/notifications/${notificationId}/read`);

      // Update local state
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, is_read: true, read_at: new Date().toISOString() }
            : notif
        )
      );

      // Update notification count
      if (onNotificationCountChange) {
        onNotificationCountChange(Math.max(0, notificationCount - 1));
      }

    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await API.put('/notifications/mark-all-read');

      // Update local state
      setNotifications(prev =>
        prev.map(notif => ({
          ...notif,
          is_read: true,
          read_at: new Date().toISOString()
        }))
      );

      // Update notification count
      if (onNotificationCountChange) {
        onNotificationCountChange(0);
      }

    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'news':
        return <FaNewspaper className="text-info" />;
      case 'order_status_change':
        return <FaShoppingCart className="text-success" />;
      case 'system':
        return <FaExclamationTriangle className="text-warning" />;
      case 'payment':
        return <FaShoppingCart className="text-primary" />;
      default:
        return <FaBell className="text-secondary" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop === clientHeight && hasMore && !loading) {
      loadNotifications(false);
    }
  };

  return (
    <Dropdown
      isOpen={isOpen}
      toggle={toggleDropdown}
      className="notification-dropdown"
      ref={dropdownRef}
    >
      <DropdownToggle
        nav
        caret={false}
        className="notification-link"
        style={{ border: 'none', background: 'transparent' }}
      >
        <FaBell className="notification-icon" />
        {notificationCount > 0 && (
          <span
            className="notification-badge"
            onClick={(e) => {
              e.stopPropagation();
              markAllAsRead();
            }}
            style={{ cursor: 'pointer' }}
            title="Mark all as read"
          ></span>
        )}
      </DropdownToggle>

      <DropdownMenu
        end
        className="notification-dropdown-menu"
        style={{
          width: '380px',
          maxHeight: '500px',
          overflowY: 'auto',
          padding: 0
        }}
        onScroll={handleScroll}
      >
        {/* Header */}
        <div className="notification-header px-3 py-2 border-bottom bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              Notifications
              {notificationCount > 0 && (
                <Badge color="danger" className="ms-2">{notificationCount}</Badge>
              )}
            </h6>
            {notificationCount > 0 && (
              <Button
                color="link"
                size="sm"
                className="text-decoration-none p-0"
                onClick={markAllAsRead}
                title="Mark all as read"
              >
                <FaCheckDouble size={14} />
              </Button>
            )}
          </div>
        </div>

        {/* Loading */}
        {loading && notifications.length === 0 && (
          <div className="text-center p-4">
            <Spinner size="sm" />
            <div className="mt-2">Loading notifications...</div>
          </div>
        )}

        {/* Notifications List */}
        {notifications.length > 0 ? (
          <>
            {notifications.map((notification) => (
              <DropdownItem
                key={notification.id}
                className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                style={{
                  whiteSpace: 'normal',
                  padding: '12px 16px',
                  borderBottom: '1px solid #eee',
                  backgroundColor: !notification.is_read ? '#f8f9fa' : 'white'
                }}
                onClick={() => !notification.is_read && markAsRead(notification.id)}
              >
                {/* Header with icon, title and mark read button */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center flex-grow-1">
                    <div style={{ paddingRight: '10px', position: 'relative' }}>
                      {getNotificationIcon(notification.type)}
                      {!notification.is_read && (
                        <span
                          style={{
                            position: 'absolute',
                            top: '-2px',
                            right: '2px',
                            backgroundColor: '#dc3545',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            border: '2px solid white'
                          }}
                        ></span>
                      )}
                    </div>
                    <h6 className={`mb-0 flex-grow-1 ${!notification.is_read ? 'fw-bold' : ''}`}>
                      {notification.title}
                    </h6>
                  </div>
                  {!notification.is_read && (
                    <Button
                      color="link"
                      size="sm"
                      className="text-success p-0 ms-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notification.id);
                      }}
                      title="Mark as read"
                    >
                      <FaCheck size={12} />
                    </Button>
                  )}
                </div>

                {/* Content */}
                <div className="text-muted small mb-1">
                  {notification.message}
                </div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {formatTime(notification.created_at)}
                </div>
              </DropdownItem>
            ))}

            {/* Load More */}
            {loading && notifications.length > 0 && (
              <DropdownItem className="text-center py-2">
                <Spinner size="sm" />
              </DropdownItem>
            )}

            {!hasMore && notifications.length > 0 && (
              <DropdownItem className="text-center text-muted py-2" disabled>
                No more notifications
              </DropdownItem>
            )}
          </>
        ) : !loading && (
          <DropdownItem className="text-center py-4" disabled>
            <div className="text-muted">
              <FaBell size={24} className="mb-2" />
              <div>No notifications yet</div>
            </div>
          </DropdownItem>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

export default NotificationsDropdown;